// Client portal Postage Discrepancies tab — read-only list of shipping
// adjustments that get added to the client's bill.
//
// The /me endpoint also strips the internal FedEx invoice number out
// (source_invoice_number), so the client sees source = 'FedEx' or
// 'Manual' without the audit-trail identifier.

import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, Loader2 } from 'lucide-react'
import { getMeDiscrepancies } from '../../../lib/api'
import { formatCurrency, formatDate } from './helpers'

function nowYM() {
  const d = new Date()
  return { year: d.getFullYear(), month: d.getMonth() + 1 }
}

export default function DiscrepanciesTab({ clientId }) {
  const [{ year, month }, setPeriod] = useState(nowYM())
  const [allPeriods, setAllPeriods]   = useState(false)

  const params = useMemo(() => allPeriods
    ? { all_periods: true }
    : { year, month }, [allPeriods, year, month])

  const { data: rows = [], isLoading, isError, error } = useQuery({
    queryKey: ['me', 'discrepancies', clientId, params],
    queryFn:  () => getMeDiscrepancies(clientId, params),
    enabled:  !!clientId,
  })

  const total = rows.reduce((s, r) => s + (Number(r.additional_charge) || 0), 0)
  const monthLabel = new Date(year, month - 1, 1).toLocaleDateString(
    'en-AU', { year: 'numeric', month: 'long' },
  )

  return (
    <div className="space-y-4">
      <div className="card p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-700">Postage discrepancies</h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Shipping cost adjustments applied to your invoice. FedEx-sourced
              rows are automatic carryovers from prior months that couldn't be
              re-billed at the time.
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <button
              onClick={() => setAllPeriods(false) || setPeriod(({ year, month }) => {
                const m = month === 1 ? 12 : month - 1
                const y = month === 1 ? year - 1 : year
                return { year: y, month: m }
              })}
              disabled={allPeriods}
              className="px-2 py-1 rounded text-slate-500 hover:bg-slate-100 disabled:opacity-30"
            >‹</button>
            <span className="text-slate-700 font-medium min-w-[100px] text-center">
              {allPeriods ? 'All time' : monthLabel}
            </span>
            <button
              onClick={() => setAllPeriods(false) || setPeriod(({ year, month }) => {
                const m = month === 12 ? 1 : month + 1
                const y = month === 12 ? year + 1 : year
                return { year: y, month: m }
              })}
              disabled={allPeriods}
              className="px-2 py-1 rounded text-slate-500 hover:bg-slate-100 disabled:opacity-30"
            >›</button>
            <button
              onClick={() => setAllPeriods(a => !a)}
              className={`ml-2 px-2 py-1 rounded text-xs ${allPeriods ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            >
              {allPeriods ? 'Showing all' : 'Show all periods'}
            </button>
          </div>
        </div>
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12 gap-2 text-slate-400">
            <Loader2 size={18} className="animate-spin" />
            <span className="text-sm">Loading…</span>
          </div>
        ) : isError ? (
          // Distinguish a fetch failure from an actual empty period — the
          // "No discrepancies" copy is reassuring if it's true, misleading
          // if we simply couldn't reach the endpoint.
          <div className="p-4 text-sm text-red-700 bg-red-50 border-t border-red-200 flex items-start gap-2">
            <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
            <span>Failed to load: {error?.response?.data?.detail || error?.message || 'Unknown error'}</span>
          </div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-400">No discrepancies in this period.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="px-4 py-2.5">Ship Date</th>
                  <th className="px-4 py-2.5">Order #</th>
                  <th className="px-4 py-2.5">Source</th>
                  <th className="px-4 py-2.5">Carrier</th>
                  <th className="px-4 py-2.5">Recipient</th>
                  <th className="px-4 py-2.5 text-right">Orig. Shipping</th>
                  <th className="px-4 py-2.5 text-right">Discrepancy</th>
                  <th className="px-4 py-2.5">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r, i) => (
                  <tr key={r.id || i} className={i % 2 === 1 ? 'bg-slate-50/40' : ''}>
                    <td className="px-4 py-2 text-slate-600">{formatDate(r.shipped_date)}</td>
                    <td className="px-4 py-2 font-mono text-xs text-slate-700">{r.order_number}</td>
                    <td className="px-4 py-2">
                      {r.source === 'fedex_reconciliation'
                        ? <span className="text-xs px-1.5 py-0.5 rounded bg-orange-100 text-orange-700">FedEx</span>
                        : <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">Manual</span>}
                    </td>
                    <td className="px-4 py-2 text-slate-600">{r.carrier || '—'}</td>
                    <td className="px-4 py-2 text-slate-600">
                      {r.recipient_name
                        ? `${r.recipient_name}${r.recipient_state ? ` (${r.recipient_state})` : ''}`
                        : '—'}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-slate-600">{formatCurrency(r.original_shipping_cost)}</td>
                    <td className={`px-4 py-2 text-right tabular-nums font-semibold ${Number(r.additional_charge) < 0 ? 'text-emerald-700' : 'text-amber-700'}`}>
                      {formatCurrency(r.additional_charge)}
                    </td>
                    <td className="px-4 py-2 text-slate-500 max-w-xs truncate" title={r.notes || ''}>{r.notes || '—'}</td>
                  </tr>
                ))}
                <tr className="bg-slate-50 border-t-2 border-slate-200">
                  <td colSpan={6} className="px-4 py-2.5 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Total ({rows.length} {rows.length === 1 ? 'discrepancy' : 'discrepancies'})
                  </td>
                  <td className={`px-4 py-2.5 text-right font-bold tabular-nums ${total < 0 ? 'text-emerald-700' : 'text-amber-700'}`}>{formatCurrency(total)}</td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
